import { confirmSignUp as ampConfirmSignUp, resendSignUpCode as ampResendSignUpCode, resetPassword as ampResetPassword, signUp as ampSignUp, confirmResetPassword, confirmSignIn, fetchAuthSession, getCurrentUser, signIn, signOut, updatePassword } from "aws-amplify/auth";
import { Amplify } from "aws-amplify";
import { showErrorNotification, showInfoNotification, showSuccessNotification } from "../shared/Notifications";
import { GetMeDocument } from "../graphql/graphql";
import { createUrqlClient } from "../urqlClient";
import { getLocalStorage, setLocalStorage } from "./LocalStorage.utils";
if (import.meta.env.VITE_USER_POOL_ID &&
    import.meta.env.VITE_CLIENT_ID &&
    import.meta.env.VITE_COGNITO_ENDPOINT) {
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: import.meta.env.VITE_USER_POOL_ID,
                userPoolClientId: import.meta.env.VITE_CLIENT_ID,
                userPoolEndpoint: import.meta.env.VITE_COGNITO_ENDPOINT
            }
        }
    });
}
const notAdminUserError = { message: "This user is not a Therapprove Admin." };
const sessionDataStorageKey = `${import.meta.env.VITE_SESSION_PREFIX}-session-data`;
const idTokenStorageKey = `${import.meta.env.VITE_SESSION_PREFIX}-id-token`;
const meDataStorageKey = `${import.meta.env.VITE_SESSION_PREFIX}-me-data`;
const permissionsDataStorageKey = `${import.meta.env.VITE_SESSION_PREFIX}-permissions-data`;
const clearStoredAuthState = () => {
    setLocalStorage(sessionDataStorageKey, null);
    setLocalStorage(idTokenStorageKey, null);
    setLocalStorage(meDataStorageKey, null);
    setLocalStorage(permissionsDataStorageKey, null);
};
const syncStoredSessionFromCognito = async () => {
    try {
        await getCurrentUser();
        const session = await fetchAuthSession();
        const data = session.tokens?.idToken?.payload;
        const idToken = session.tokens?.idToken?.toString();
        if (!data || !idToken) {
            throw new Error("No active Cognito session found.");
        }
        const existingSessionData = getLocalStorage(sessionDataStorageKey);
        setLocalStorage(sessionDataStorageKey, data);
        setLocalStorage(idTokenStorageKey, idToken);
        if (!existingSessionData || existingSessionData.sub !== data.sub) {
            setLocalStorage(meDataStorageKey, null);
            setLocalStorage(permissionsDataStorageKey, null);
        }
        return {
            data,
            idToken
        };
    }
    catch {
        clearStoredAuthState();
        return null;
    }
};
export const bootstrapCurrentUser = async () => {
    const client = createUrqlClient();
    const { data, error } = await client
        .query(GetMeDocument, {}, { requestPolicy: "network-only" })
        .toPromise();
    if (error) {
        throw error;
    }
    const me = data?.users?.me;
    if (!me) {
        throw new Error("We couldn't load your account details after sign in. Please try again.");
    }
    setLocalStorage(meDataStorageKey, me);
    setLocalStorage(permissionsDataStorageKey, me.permissions ?? []);
    return me;
};
export class Auth {
    constructor() {
        if (!getLocalStorage(sessionDataStorageKey)) {
            clearStoredAuthState();
        }
    }
    async syncStoredSessionFromCognito() {
        return await syncStoredSessionFromCognito();
    }
    /**
     * Authenticate the user using Amplify.
     * If a new password is required, handles the new password challenge.
     */
    async authenticate({ username, password, newPassword, requireAdmin = false }) {
        try {
            let isAlreadySignedIn = false;
            try {
                const user = await getCurrentUser();
                isAlreadySignedIn =
                    user && user?.signInDetails?.loginId === username;
            }
            catch {
                isAlreadySignedIn = false;
            }
            // If the user is not already signed in, sign them in
            if (!isAlreadySignedIn && username && password) {
                const { nextStep } = await signIn({
                    username,
                    password
                });
                const signInStep = nextStep.signInStep;
                const needsPasswordReset = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" === signInStep;
                if (signInStep === "RESET_PASSWORD") {
                    throw new Error("Password reset is required. Please use the forgot password flow to reset your password.");
                }
                if (needsPasswordReset && newPassword) {
                    // Complete the new password challenge if required
                    await confirmSignIn({
                        challengeResponse: newPassword
                    });
                }
                else if (needsPasswordReset && !newPassword) {
                    // If a new password is required but not provided, return the new password required flag
                    return { new_password_required: true }; // Return flag to indicate new password is required
                }
            }
            const syncedSession = await syncStoredSessionFromCognito();
            if (!syncedSession) {
                throw new Error("We couldn't restore your sign-in session. Please try again.");
            }
            const { data } = syncedSession;
            // Admin app: verify the user belongs to the admin Cognito group
            if (requireAdmin && !this.isAdminUser(data)) {
                await this.logout();
                throw notAdminUserError;
            }
            // Save session data and resolve the authentication
            await bootstrapCurrentUser();
            return data;
        }
        catch (error) {
            console.error("Authentication failed:", error);
            clearStoredAuthState();
            // Sometimes the user gets stuck in a signed in state, this will log them out if needed
            if (error instanceof Error &&
                error.message.includes("There is already a signed in user")) {
                showInfoNotification("You are already signed in, but we have logged you out. Please try again.");
                await this.logout();
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Changes the current user's password.
     * User must provide the previous and proposed passwords.
     */
    async changePassword(PreviousPassword, ProposedPassword) {
        try {
            return await updatePassword({
                oldPassword: PreviousPassword,
                newPassword: ProposedPassword
            });
        }
        catch (error) {
            console.error("Error changing password:", error);
            throw error;
        }
    }
    /**
     * Initiates forgot password process by sending a verification code to the user's email.
     */
    async sendVerificationCode(username, setForgotPasswordPhase) {
        try {
            await ampResetPassword({
                username: username
            });
            setForgotPasswordPhase(1); // Update UI phase to show verification code input
        }
        catch (error) {
            console.error("Failed to send verification code:", error);
            showErrorNotification(error instanceof Error
                ? error.message
                : "An unknown error occurred");
        }
    }
    /**
     * Resets the user's password after receiving the verification code.
     */
    async resetPassword(username, code, password) {
        try {
            await confirmResetPassword({
                username,
                newPassword: password,
                confirmationCode: code
            });
            showSuccessNotification("Password reset successful!");
            return true;
        }
        catch (error) {
            showErrorNotification(error instanceof Error
                ? error.message
                : "An unknown error occurred");
            throw error;
        }
    }
    async signUp({ username, password, userAttributes }) {
        try {
            return await ampSignUp({
                username,
                password,
                options: {
                    userAttributes
                }
            });
        }
        catch (error) {
            console.error("Failed to sign up:", error);
            throw error;
        }
    }
    async confirmSignUp(username, confirmationCode) {
        try {
            return await ampConfirmSignUp({
                username,
                confirmationCode
            });
        }
        catch (error) {
            console.error("Failed to confirm sign up:", error);
            throw error;
        }
    }
    async resendSignUpCode(username) {
        try {
            return await ampResendSignUpCode({ username });
        }
        catch (error) {
            console.error("Failed to resend sign up code:", error);
            throw error;
        }
    }
    /**
     * Retrieves a specific token (ID or access token) from the current session.
     */
    async getSessionToken(tokenType) {
        try {
            const session = await fetchAuthSession();
            if (tokenType === "idToken") {
                return session.tokens?.idToken?.toString();
            }
            return session.tokens?.accessToken?.toString();
        }
        catch (error) {
            console.error(`Error retrieving ${tokenType} token:`, error);
            throw error;
        }
    }
    /**
     * Retrieves new ID and access tokens when they expire.
     */
    async retrieveNewTokens() {
        try {
            const session = await fetchAuthSession();
            const data = session.tokens?.idToken?.payload;
            setLocalStorage(sessionDataStorageKey, data);
            setLocalStorage(idTokenStorageKey, session.tokens?.idToken?.toString());
            return session;
        }
        catch (error) {
            console.error("Token refresh failed:", error);
            throw error;
        }
    }
    /**
     * Logs the user out by clearing session data from local storage and signing out.
     */
    async logout() {
        try {
            await signOut({ global: true });
        }
        catch (error) {
            console.error("Error during logout:", error);
        }
        finally {
            clearStoredAuthState();
        }
    }
    /**
     * Retrieves a specific property from the decoded ID token in the session.
     */
    getSessionProperty(sessionData, propertyName) {
        return sessionData[propertyName] ?? null;
    }
    /**
     * Verifies if the user belongs to the Admin group as specified in Cognito.
     */
    isAdminUser(sessionData) {
        return (sessionData["cognito:groups"] &&
            sessionData["cognito:groups"].includes(import.meta.env.VITE_ADMIN_GROUP));
    }
}
